import { useState } from "react";
import dayjs from "dayjs";

import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import Table from "react-bootstrap/Table";
import { FaEllipsisV, FaEye, FaInfoCircle } from "react-icons/fa";

import { formatIdentifier } from "@/utils/formatIdentifier";
import { handleColumnToggle } from "@/utils/handleColumnTogle";
import { getAge } from "@/utils/dateUtil";
import { getCitizenScoreStatusDescriptionIndicatorC2 } from "@/utils/indicatorsEsf";
import { DetailedCitizenAttendancesIndicatorC2Data } from "@/services/esus/indicator/esf/c2/getDetailedCitizensAttendancesIndicatorC2";

import { TableSkeletons } from "@/components/TableSkeletons";
import { CardOverlayTrigger } from "@/components/CardOverlayTrigger";

type Column = {
	title: string;
	visible: boolean;
};

type IndicatorEsfC2AttendanceTableColumns = {
	citizenName: Column;
	citizenAge: Column;
	document: Column;
	motherName: Column;
	healthTeam: Column;
	microarea: Column;
	firstAttendance: Column;
	numberOfAttendances: Column;
	metricsRegistersAttendances: Column;
};

type IndicatorEsfC2CitizensAttendancesTableProps = {
	citizensWithAttendances: DetailedCitizenAttendancesIndicatorC2Data[];
	isLoading: boolean;
	openCitizenHistory: (citizenWithAttendances: DetailedCitizenAttendancesIndicatorC2Data) => void;
};

export function IndicatorEsfC2CitizensAttendancesTable({
	citizensWithAttendances,
	isLoading,
	openCitizenHistory,
}: Readonly<IndicatorEsfC2CitizensAttendancesTableProps>) {
	const [columns, setColumns] = useState<IndicatorEsfC2AttendanceTableColumns>({
		citizenName: { title: "Nome do cidadão", visible: true },
		citizenAge: { title: "Idade", visible: true },
		document: { title: "Documento", visible: true },
		motherName: { title: "Nome da mãe", visible: true },
		healthTeam: { title: "Equipe", visible: false },
		microarea: { title: "MA", visible: false },
		firstAttendance: { title: "1ª Consulta", visible: true },
		numberOfAttendances: { title: "Consultas", visible: true },
		metricsRegistersAttendances: { title: "Registros Peso/Altura", visible: true },
	});

	return (
		<Table striped bordered hover responsive>
			<thead className="text-center fs-7">
				<tr className="fw-bolder text-center">
					<th className={`text-start px-2 ${!columns.citizenName.visible && "d-none"}`}>
						{columns.citizenName.title}
					</th>
					<th className={`px-2 ${!columns.citizenAge.visible && "d-none"}`}>
						{columns.citizenAge.title}
					</th>
					<th className={`px-2 ${!columns.document.visible && "d-none"}`}>
						{columns.document.title}
					</th>
					<th className={`px-2 ${!columns.motherName.visible && "d-none"}`}>
						{columns.motherName.title}
					</th>
					<th className={`px-2 ${!columns.healthTeam.visible && "d-none"}`}>
						{columns.healthTeam.title}
					</th>
					<th className={`px-2 ${!columns.microarea.visible && "d-none"}`}>
						{columns.microarea.title}
					</th>
					<th className={`px-2 ${!columns.firstAttendance.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.firstAttendance.title}
							<CardOverlayTrigger
								header="1ª Consulta"
								message="1ª Consulta presencial realizada por médico ou enfermeiro até o 30º dia de vida"
								icon={<FaInfoCircle className="text-muted" />}
							/>
						</div>
					</th>
					<th className={`px-2 ${!columns.numberOfAttendances.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.numberOfAttendances.title}
							<CardOverlayTrigger
								header="Consultas"
								message="Consultas realizadas por médico ou enfermeiro até os 2 anos de vida (a portaria indica que sejam realizadas 9 consultas)"
								icon={<FaInfoCircle className="text-muted" />}
							/>
						</div>
					</th>
					<th
						className={`px-2 ${
							!columns.metricsRegistersAttendances.visible && "d-none"
						}`}
					>
						<div className="d-flex flex-column">
							{columns.metricsRegistersAttendances.title}
							<CardOverlayTrigger
								header="Registros de peso e altura"
								message="Atendimentos individuais ou procedimentos realizados até os 2 anos de vida que constem informações de peso e altura (a portaria indica que sejam realizados 9 registros)"
								icon={<FaInfoCircle className="text-muted" />}
							/>
						</div>
					</th>
					<th className="text-nowrap pb-0">
						Ações
						<OverlayTrigger
							trigger="click"
							placement="bottom"
							overlay={
								<Popover>
									<Popover.Body>
										<Form className="d-flex flex-column gap-2">
											{Object.keys(columns).map((key, index) => (
												<Form.Check
													key={`citizen-attendances-table-column-${
														index + 1
													}`}
													type="checkbox"
													label={
														columns[
															key as keyof IndicatorEsfC2AttendanceTableColumns
														].title
													}
													checked={
														columns[
															key as keyof IndicatorEsfC2AttendanceTableColumns
														].visible
													}
													onChange={() =>
														handleColumnToggle(
															key as keyof IndicatorEsfC2AttendanceTableColumns,
															setColumns
														)
													}
												/>
											))}
										</Form>
									</Popover.Body>
								</Popover>
							}
							rootClose
						>
							<Button
								title="Exibição de colunas da tabela"
								variant="white"
								className="px-2"
								disabled={isLoading}
							>
								<FaEllipsisV />
							</Button>
						</OverlayTrigger>
					</th>
				</tr>
			</thead>
			<tbody className="fs-8">
				{!isLoading && citizensWithAttendances.length > 0
					? citizensWithAttendances.map((citizenWithAttendances, index) => (
							<tr key={`citizen-attendances-table-row-${index + 1}`}>
								<td className={`px-2 ${!columns.citizenName.visible && "d-none"}`}>
									<div className="d-flex flex-column">
										<div>{citizenWithAttendances.fullName}</div>
										<div className="text-muted">
											{citizenWithAttendances.birthdate
												? dayjs(citizenWithAttendances.birthdate).format(
														"DD/MM/YYYY"
												  )
												: ""}
										</div>
									</div>
								</td>
								<td
									className={`text-center text-nowrap px-2 ${
										!columns.citizenAge.visible && "d-none"
									}`}
								>
									{citizenWithAttendances.birthdate
										? getAge(citizenWithAttendances.birthdate)
										: ""}
								</td>
								<td
									className={`text-center text-nowrap px-2 ${
										!columns.document.visible && "d-none"
									}`}
								>
									{citizenWithAttendances.cpf
										? formatIdentifier(citizenWithAttendances.cpf)
										: citizenWithAttendances.cns ?? ""}
								</td>
								<td
									className={`text-center px-2 ${
										!columns.motherName.visible && "d-none"
									}`}
								>
									{citizenWithAttendances.motherName}
								</td>
								<td className={`px-2 ${!columns.healthTeam.visible && "d-none"}`}>
									<div className="d-flex flex-column">
										<div>{citizenWithAttendances.healthTeam?.name ?? ""}</div>
										<div className="text-muted">
											{citizenWithAttendances.healthTeam?.ine ?? ""}
										</div>
									</div>
								</td>
								<td
									className={`text-center text-nowrap px-2 ${
										!columns.microarea.visible && "d-none"
									}`}
								>
									{citizenWithAttendances.area ?? "FA"}
								</td>
								<td
									className={`text-center px-2 ${
										!columns.firstAttendance.visible && "d-none"
									}`}
								>
									<div className="d-flex flex-column">
										<div>
											{getCitizenScoreStatusDescriptionIndicatorC2(
												citizenWithAttendances.firstAttendanceStatus
											)}
										</div>
										<div className="text-muted">
											{citizenWithAttendances.firstAttendance?.date
												? dayjs(
														citizenWithAttendances.firstAttendance.date
												  ).format("DD/MM/YYYY")
												: ""}
										</div>
									</div>
								</td>
								<td
									className={`text-center px-2 ${
										!columns.numberOfAttendances.visible && "d-none"
									}`}
								>
									{citizenWithAttendances.attendances?.length ?? 0}
								</td>
								<td
									className={`text-center px-2 ${
										!columns.metricsRegistersAttendances.visible && "d-none"
									}`}
								>
									{citizenWithAttendances.weightAndHeightMetricsRegisters
										?.length ?? 0}
								</td>

								<td className="text-center">
									<Button
										className="bg-primary py-1 px-3"
										onClick={() => openCitizenHistory(citizenWithAttendances)}
										title="Histórico do cidadão"
									>
										<FaEye className="fs-4" />
									</Button>
								</td>
							</tr>
					  ))
					: isLoading && (
							<TableSkeletons
								numberOfCells={
									Object.values(columns).reduce(
										(count, column) =>
											count + (column?.visible === true ? 1 : 0),
										0
									) + 1
								}
							/>
					  )}
			</tbody>
		</Table>
	);
}
